import {
  Box,
  Divider,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  calendarOptions,
  detailsTickets,
  setDetailsData,
  tripData,
} from 'src/redux/Calendar/sliceCalendar';
import ForwardIcon from '@material-ui/icons/Forward';
import {
  IOptionDetails,
  IOptionsDetailsData,
  IOtionDetailsValue,
} from 'src/redux/Calendar/interfaces';
import { getPlaceholderFromDefaultValues } from 'src/helpers/calendar/getNormalisedValueFromInput';
import { useStyles } from './styles';

interface IOptionMaster extends Omit<IOptionDetails, 'calendar_id'> {}

export const OptionsMasterUpdateForm = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const isMobile = useMediaQuery('(max-width: 960px)');
  const { option_type } = useSelector(calendarOptions);
  const data = useSelector(tripData);
  const detailedData: IOptionsDetailsData | undefined =
    useSelector(detailsTickets);
  const defaultOption = data?.additional_options.find(
    (i) => i.id === option_type
  );

  const [state, setState] = useState<IOptionMaster>({
    is_disabled: '0',
    values:
      defaultOption?.values.map(({ id, is_disabled, apply_tax }) => ({
        id: id.toString(),
        price: null,
        is_disabled: is_disabled ? '1' : '0',
        apply_tax: apply_tax ? '1' : '0',
      })) || [],
  });
  if (!defaultOption) return null;

  const changeState = (data: Partial<IOptionMaster>) => {
    setState((prev) => ({ ...prev, ...data }));
  };
  const changeStateValues = (
    data: Partial<IOtionDetailsValue>,
    idx: number
  ) => {
    setState((prev) => ({
      ...prev,
      values: prev.values.map((i, pos) => ({
        ...i,
        ...(pos === idx && data),
      })),
    }));
  };

  const applyChanges = (name: keyof IOptionMaster) => {
    if (!detailedData) return;
    dispatch(
      setDetailsData(
        Object.keys(detailedData).reduce(
          (acc: IOptionsDetailsData, curr) => ({
            ...acc,
            [curr]: {
              ...detailedData[curr],
              [name]: state[name],
            },
          }),
          {}
        )
      )
    );
  };
  const applyValuesChanges = (name: keyof IOtionDetailsValue, idx: number) => {
    if (!detailedData) return;
    dispatch(
      setDetailsData(
        Object.keys(detailedData).reduce(
          (acc: IOptionsDetailsData, curr) => ({
            ...acc,
            [curr]: {
              ...detailedData[curr],
              values: detailedData[curr].values.map((i, pos) => ({
                ...i,
                ...(pos === idx && {
                  [name as keyof IOptionsDetailsData]: state.values[idx][name],
                }),
              })),
            },
          }),
          {}
        )
      )
    );
  };

  return (
    <Box
      bgcolor="#c0f3ef"
      borderBottom="0.8px solid rgba(0, 0, 0, 0.12)"
      px={3}>
      <Box py={0.5} display="flex" alignItems="center">
        <Typography variant="button" style={{ fontWeight: 500 }}>
          Bulk Update for All Days
        </Typography>
      </Box>
      <Divider variant="fullWidth" />
      <Box
        display="flex"
        width={1}
        justifyContent="center"
        alignItems="center"
        flexWrap={'wrap'}
        gridGap="10px">
        <Box mb={2} pr={4}>
          <Typography
            variant="h6"
            style={{
              fontWeight: 600,
              padding: '8px 0',
            }}>
            {defaultOption.name}
          </Typography>
          <Box display="flex" alignItems="center" py={1}>
            <FormControlLabel
              control={<Switch className={classes.customSwitch} />}
              label="Disable Option"
              checked={Boolean(state.is_disabled && +state.is_disabled)}
              onChange={(_, checked) =>
                changeState({ is_disabled: checked ? '1' : '0' })
              }
            />
            <Box
              style={{ cursor: 'pointer' }}
              display="flex"
              onClick={() => applyChanges('is_disabled')}>
              <ForwardIcon style={{ rotate: '90deg' }} />
            </Box>
          </Box>
        </Box>
        <Box py={2}>
          {state.values.map((i, idx) => (
            <Box
              key={i.id}
              display="flex"
              justifyContent="space-between"
              flexWrap="wrap"
              gridGap="10px"
              py={1.5}
              borderTop={idx ? '0.8px solid rgba(0, 0, 0, 0.12)' : undefined}>
              <Box
                mx={4}
                display="flex"
                alignItems="center"
                fontWeight={500}
                minWidth={'100px'}>
                {defaultOption.values.find((o) => o.id === +i.id)?.name || '-'}
              </Box>
              <Box mr={2} display="flex" alignItems="center">
                <TextField
                  type="number"
                  placeholder={getPlaceholderFromDefaultValues(
                    defaultOption.values.find((o) => o.id === +i.id)?.price ||
                      0,
                    2
                  )}
                  value={i.price || ''}
                  onChange={(e) =>
                    changeStateValues({ price: e.target.value }, idx)
                  }
                  variant="outlined"
                  className={classes.inputWrapper}
                  InputProps={{
                    style: {
                      width: '120px',
                      height: '40px',
                      background: '#fff',
                    },
                  }}
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                    style: {
                      letterSpacing: '0.04em',
                    },
                  }}
                  label="Price"
                />
                <Box
                  style={{ cursor: 'pointer' }}
                  display="flex"
                  ml={0.5}
                  onClick={() => applyValuesChanges('price', idx)}>
                  <ForwardIcon style={{ rotate: '90deg' }} />
                </Box>
              </Box>
              <Box display="flex">
                <Box display="flex" alignItems="center" mr={4}>
                  <FormControlLabel
                    control={<Switch className={classes.customSwitch} />}
                    label="Disable"
                    checked={Boolean(i.is_disabled && +i.is_disabled)}
                    onChange={(_, checked) =>
                      changeStateValues(
                        { is_disabled: checked ? '1' : '0' },
                        idx
                      )
                    }
                  />
                  <Box
                    style={{ cursor: 'pointer' }}
                    display="flex"
                    onClick={() => applyValuesChanges('is_disabled', idx)}>
                    <ForwardIcon style={{ rotate: '90deg' }} />
                  </Box>
                </Box>
                {/* <Box display="flex" alignItems="center">
                  <FormControlLabel
                    control={<Switch className={classes.customSwitch} />}
                    label="Apply Tax"
                    checked={Boolean(i.apply_tax && +i.apply_tax)}
                    onChange={(_, checked) =>
                      changeStateValues({ apply_tax: checked ? '1' : '0' }, idx)
                    }
                  />
                  <Box
                    style={{ cursor: 'pointer' }}
                    display="flex"
                    onClick={() => applyValuesChanges('apply_tax', idx)}>
                    <ForwardIcon style={{ rotate: '90deg' }} />
                  </Box>
                </Box> */}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};
